import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Alert,
    CircularProgress,
    useTheme,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Fab,
} from "@mui/material";
import QRCode from "qrcode";
import FMBlogo from "../../../assests/FMBlogo.png";
import { getCurrentBranchId, getCurrentUser } from "../../../utils/auth";
import { getQRCodes, createQRCode } from "../../../api/api";
import SettingsIcon from "@mui/icons-material/Settings";
import CloseIcon from "@mui/icons-material/Close";
import config from "../../../config/config";
import { getFrontendUrl } from "../../../config/config";
const QRPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [qrCode, setQrCode] = useState("");
    const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [currentQRCode, setCurrentQRCode] = useState("");

    const branchId = getCurrentBranchId();
    const currentUser = getCurrentUser();

    // Initialize logo URL correctly from the start to prevent flash
    const [logoUrl, setLogoUrl] = useState(() => {
        if (currentUser && currentUser.logo) {
            const logoFilename = currentUser.logo;
            return getFrontendUrl(`/storage/logos/${logoFilename}`);
        }
        return FMBlogo;
    });

    // Update logo URL if currentUser changes
    useEffect(() => {
        if (currentUser && currentUser.logo) {
            // Construct logo URL from storage
            const logoFilename = currentUser.logo;
            const dynamicLogoUrl = getFrontendUrl(`/storage/logos/${logoFilename}`);
            setLogoUrl(dynamicLogoUrl);
        } else {
            // Fallback to default logo if no user logo is available
            setLogoUrl(FMBlogo);
        }
    }, [currentUser]);

    // Generate QR Code
    const generateQRCode = async () => {
        if (!qrCode.trim()) {
            setError("Please enter a code");
            return;
        }

        setLoading(true);
        setError("");
        setSuccess("");

        try {
            // Create the full URL for the QR code
            const fullUrl = `${'https://easyque.eu'}/getticket/${qrCode}`;

            // Generate QR code data URL
            const dataUrl = await QRCode.toDataURL(fullUrl, {
                width: 300,
                margin: 2,
                color: {
                    dark: "#000000",
                    light: "#FFFFFF",
                },
            });

            setQrCodeDataUrl(dataUrl);
            setCurrentQRCode(qrCode);

            // Save to database
            await createQRCode({
                code: qrCode,
                branch_id: branchId,
                qr_data_url: dataUrl,
            });

            setSuccess(`QR Code generated and saved successfully!`);
            setModalOpen(false); // Close modal after success
        } catch (err) {
            console.error("Error generating QR code:", err);
            
            // Extract specific error message from backend response
            let errorMessage = "Failed to generate QR code";
            
            if (err.response?.data?.errors) {
                // Handle validation errors (422)
                const errors = err.response.data.errors;
                if (errors.code) {
                    errorMessage = errors.code[0]; // Get first error message for code field
                } else if (errors.branch_id) {
                    errorMessage = errors.branch_id[0];
                } else {
                    // Get first error from any field
                    const firstErrorKey = Object.keys(errors)[0];
                    errorMessage = errors[firstErrorKey][0];
                }
            } else if (err.response?.data?.message) {
                // Handle general error messages
                errorMessage = err.response.data.message;
            } else if (err.message) {
                // Handle network or other errors
                errorMessage = err.message;
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Load current QR code from database
    const loadCurrentQRCode = async () => {
        try {
            const data = await getQRCodes(branchId);
            if (data && data.length > 0) {
                const currentCode =
                    data.find((code) => code.is_active) || data[0];
                if (currentCode.qr_data_url) {
                    setQrCodeDataUrl(currentCode.qr_data_url);
                    setCurrentQRCode(currentCode.code);
                }
            }
        } catch (err) {
            console.error("Error loading current QR code:", err);
        }
    };

    // Load existing codes on component mount
    useEffect(() => {
        loadCurrentQRCode();
    }, []);

    // Clear messages after 5 seconds
    useEffect(() => {
        if (success || error) {
            const timer = setTimeout(() => {
                setSuccess("");
                setError("");
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [success, error]);

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="flex-start"
            bgcolor="#fff"
            px={2}
            py={4}
            minHeight="100%"
            width="100%"
            overflow="auto"
            sx={{
                height: "100%",
                maxHeight: "none",
            }}
        >
            {/* Header */}
            <Typography
                variant="h2"
                fontWeight="bold"
                gutterBottom
                sx={{ fontSize: { xs: "2rem", sm: "3rem", md: "4rem" } }}
            >
                KØLAPP
            </Typography>

            {/* Logo - Dynamic based on logged-in user */}
            <Box mb={3}>
                <img
                    src={logoUrl}
                    alt="Branch Logo"
                    style={{ width: isMobile ? 180 : 300, maxWidth: "90%" }}
                    onError={(e) => {
                        // Fallback to default logo if dynamic logo fails to load
                        e.target.src = FMBlogo;
                    }}
                />
            </Box>

            {/* QR Code Display */}
            {qrCodeDataUrl ? (
                <Box textAlign="center" mt={4}>
                    <img
                        src={qrCodeDataUrl}
                        alt="QR Code"
                        style={{
                            width: isMobile ? 350 : 600,
                            maxWidth: "100%",
                            border: "2px solid #e0e0e0",
                            borderRadius: 8,
                            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                        }}
                    />
                    <Typography
                        variant="h6"
                        fontWeight="bold"
                        mt={2}
                        color="primary"
                    >
                        Code: {currentQRCode}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={1}>
                        Scan this QR code to join the queue
                    </Typography>
                </Box>
            ) : (
                <Box textAlign="center" mt={4}>
                    <Typography variant="h6" color="text.secondary" mb={2}>
                        No QR Code Generated
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Click the settings button to generate a QR code
                    </Typography>
                </Box>
            )}

            {/* Settings FAB */}
            {/* <Fab
                color="primary"
                aria-label="settings"
                sx={{
                    position: "fixed",
                    bottom: 16,
                    right: 16,
                }}
                onClick={() => setModalOpen(true)}
            >
                <SettingsIcon />
            </Fab> */}

            {/* Settings Modal */}
            <Dialog
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Typography variant="h6">Generate QR Code</Typography>
                        <IconButton onClick={() => setModalOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Enter QR Code"
                        value={qrCode}
                        onChange={(e) => setQrCode(e.target.value)}
                        placeholder="e.g., MAIN001, DOWN001"
                        sx={{ mb: 3, mt: 2 }}
                        helperText="Enter a unique code for this QR code"
                    />

                    {/* Messages */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {success}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModalOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={generateQRCode}
                        disabled={loading || !qrCode.trim()}
                    >
                        {loading ? (
                            <>
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                Generating...
                            </>
                        ) : (
                            "Generate QR Code"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default QRPage;
